import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Clock, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";

const LocationsOverview = () => {
  const locations = [
    {
      name: "Navi Mumbai",
      slug: "navi-mumbai",
      address: "2, Plot, no. 28, Rajkamal Bayside, 202, Wing, Sector 15, CBD Belapur, Navi Mumbai, Maharashtra 400614",
      phone: "+91 87795 19634",
      hours: "Open ⋅ Closes 10:30 PM",
      mapUrl: "https://maps.app.goo.gl/RRc1DMagCPnw1zEs7",
      color: "blue"
    },
    {
      name: "Vikhroli",
      slug: "vikhroli",
      address: "E-703. Crescent Hill View, L.B.S Road Ghatkopar Vikhroli, Road, near Surya Nagar, Vikhroli West, Mumbai, Maharashtra 400083",
      phone: "+91 7977084003",
      hours: "Open ⋅ Closes 10:30 PM",
      mapUrl: "https://maps.app.goo.gl/bDoGNhvDj793oycf9",
      color: "red"
    },
    {
      name: "Santacruz",
      slug: "santacruz",
      address: "B - 704, Sagar Avenue, Rustumbha Patuck Marg, Vakola Bridge, Santacruz East, Mumbai, Maharashtra 400055",
      phone: "+91 9619847890",
      hours: "Open ⋅ Closes 10:30 PM",
      mapUrl: "https://maps.app.goo.gl/V3PPYrFRACHuEdq47",
      areasServed: "Bandra West and nearby areas",
      color: "purple"
    },
    {
      name: "Pydhonie (South Mumbai)",
      slug: "pydhonie",
      address: "10, Tawakkal Building, 21, Kolsa St, Nakhuda Mohalla, Pydhonie, Bhuleshwar, Mumbai, Maharashtra 400003",
      phone: "+91 98195 47890",
      hours: "Open ⋅ Closes 11 PM",
      mapUrl: "https://maps.app.goo.gl/h4h6Fjr6PWK8uziL6",
      color: "green"
    },
    {
      name: "Mankhurd",
      slug: "mankhurd",
      address: "Shop No. 16, 1st Floor, J Sector, B - Line, Cheeta Camp, Trombay, Mumbai, Maharashtra 400088",
      phone: "+91 84240 08891",
      hours: "Open ⋅ Closes 11 PM",
      mapUrl: "https://maps.app.goo.gl/DedF9w6MiAx34ZWj7",
      color: "orange"
    }
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      blue: "border-blue-200 bg-blue-50",
      green: "border-green-200 bg-green-50",
      purple: "border-purple-200 bg-purple-50",
      orange: "border-orange-200 bg-orange-50",
      red: "border-red-200 bg-red-50"
    };
    return colorMap[color] || "border-gray-200 bg-gray-50";
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Our Locations</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Visit any of our convenient locations across Mumbai and Navi Mumbai. 
            All locations offer the complete range of passport services with expert assistance.
          </p>
        </div>

        {/* Locations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {locations.map((location, index) => (
            <Card key={index} className="hover:shadow-lg transition-all duration-300 bg-blue-50 border-blue-200">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="text-xl text-gray-900">{location.name}</span>
                  <MapPin className="h-6 w-6 text-gray-600" />
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Address */}
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Address</p>
                  <p className="text-gray-600">{location.address}</p>
                  {location.areasServed && (
                    <p className="text-sm text-gray-500 mt-1">Areas served: {location.areasServed}</p>
                  )}
                </div>

                {/* Phone */}
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-gray-600" />
                  <a 
                    href={`tel:${location.phone}`}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {location.phone}
                  </a>
                </div>

                {/* Hours */}
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4 text-gray-600" />
                  <span className="text-gray-600">{location.hours}</span>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <Link to={`/location/${location.slug}`} className="flex-1">
                    <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                      View Details
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                  <a 
                    href={location.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1"
                  >
                    <Button variant="outline" className="w-full">
                      <MapPin className="mr-2 h-4 w-4" />
                      View on Map
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Contact Section */}
        <div className="bg-white rounded-lg p-8 shadow-md text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Not Sure Which Location to Visit?</h2>
          <p className="text-gray-600 mb-6">
            Call us and our team will help you choose the most convenient location based on your needs and proximity.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://wa.me/917977084003" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 transition-colors"
            >
              Chat on WhatsApp
            </a>
            <a 
              href="tel:+917977084003"
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              Call Us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationsOverview;